/**
 * Join Requests Widget (Owner-only)
 * - Hidden when there are no pending requests
 * - Opens automatically when new requests arrive
 */

import React, { useEffect, useRef, useState } from "react";
import "./JoinRequestsWidget.css";

export interface JoinRequestItem {
  requestId: string;
  displayName: string;
  requestedAt: string;
}

interface JoinRequestsWidgetProps {
  requests: JoinRequestItem[];
  busyId: string | null;
  onAction: (requestId: string, action: "approve" | "deny") => Promise<void> | void;
}

const JoinRequestsWidget: React.FC<JoinRequestsWidgetProps> = ({ requests, busyId, onAction }) => {
  const [open, setOpen] = useState(false);
  const [attention, setAttention] = useState(false);
  const prevCountRef = useRef(0);
  const attentionTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const prev = prevCountRef.current;
    const next = requests.length;
    prevCountRef.current = next;

    if (next === 0) {
      setOpen(false);
      setAttention(false);
      if (attentionTimeoutRef.current !== null) {
        window.clearTimeout(attentionTimeoutRef.current);
        attentionTimeoutRef.current = null;
      }
      return;
    }

    // Don't auto-open (it can cover the UI). Instead, pulse the launcher.
    if (next > prev && !open) {
      setAttention(true);
      if (attentionTimeoutRef.current !== null) {
        window.clearTimeout(attentionTimeoutRef.current);
      }
      attentionTimeoutRef.current = window.setTimeout(() => {
        attentionTimeoutRef.current = null;
        setAttention(false);
      }, 4500);
    }
  }, [open, requests.length]);

  useEffect(() => {
    return () => {
      if (attentionTimeoutRef.current !== null) {
        window.clearTimeout(attentionTimeoutRef.current);
      }
    };
  }, []);

  if (requests.length === 0) return null;

  return (
    <div className="jr-joinwidget" data-open={open ? "1" : "0"} data-attention={attention ? "1" : "0"}>
      <button
        type="button"
        className="jr-btn jr-btn--primary jr-joinwidget-launch"
        onClick={() => {
          setAttention(false);
          setOpen((v) => !v);
        }}
        title="Join requests"
      >
        Requests <span className="jr-badge">{requests.length}</span>
      </button>

      <div className="jr-joinwidget-drawer" role="dialog" aria-label="Join requests" aria-hidden={!open}>
        <div className="jr-joinwidget-header">
          <div className="jr-joinwidget-title">Join requests</div>
          <button
            type="button"
            className="jr-icon-btn jr-joinwidget-close"
            onClick={() => setOpen(false)}
            title="Close"
          >
            X
          </button>
        </div>

        <div className="jr-joinwidget-body">
          {requests.map((r) => {
            const isBusy = busyId === r.requestId;
            return (
              <div key={r.requestId} className="jr-joinwidget-item">
                <div className="jr-joinwidget-who">
                  <div className="jr-joinwidget-name">{r.displayName}</div>
                  <div className="jr-joinwidget-when">{new Date(r.requestedAt).toLocaleString()}</div>
                </div>
                <div className="jr-joinwidget-cta">
                  <button
                    type="button"
                    disabled={isBusy}
                    className="jr-btn jr-btn--success"
                    onClick={() => void onAction(r.requestId, "approve")}
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    disabled={isBusy}
                    className="jr-btn jr-btn--danger"
                    onClick={() => void onAction(r.requestId, "deny")}
                  >
                    Deny
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default JoinRequestsWidget;
